import { useEffect, useState } from "react";

export interface GlobalLogoProps {
  size?: number;
  variant?: "fullColor" | "white" | "black" | "monoBlue" | "favicon" | "animated";
  showText?: boolean;
  className?: string;
}

const GRADIENT_ID = "rfwGradient";

function RFWPath({ color = "url(#rfwGradient)", stroke }: { color?: string; stroke?: string }) {
  return (
    <g>
      <path
        d="M4 2 h32 c4 0 6 1 8 3 c2 2 3 5 3 9 c0 4 -1 7 -3 9 c-2 2 -5 3 -9 3 h-10 v-24 z M4 2 v56 h-4 v-56 h4 z"
        fill="none"
      />
      <path
        d="M4 56 h-4 v-56 h4 v56 z M4 2 h28 c4 0 7 1 9 3 c2 2 3 5 3 9 c0 4 -1 7 -3 9 c-2 2 -5 3 -9 3 h-13 v-24 z M19 26 h13 c2 0 3 0 4 -1 c1 -1 1 -2 1 -3 c0 -1 0 -2 -1 -3 c-1 -1 -2 -1 -4 -1 h-13 v8 z M14 32 v-8 h-10 v-22 h28 c4 0 7 1 9 3 c2 2 3 5 3 9 c0 4 -1 7 -3 9 c-2 2 -5 3 -9 3 h-13 v24 h-5 v-24 h-10 z M14 38 v18 h-5 v-18 h5 z M14 56 v-18 h-10 v-26 h28 c4 0 6 1 8 3 c2 2 3 5 3 9 c0 4 -1 7 -3 9 c-2 2 -5 3 -9 3 h-13 v-24 h-5 v24 z M14 8 v18 h28 c2 0 3 0 4 -1 c1 -1 1 -2 1 -3 c0 -1 0 -2 -1 -3 c-1 -1 -2 -1 -4 -1 h-28 z"
        fill={color}
        stroke={stroke}
        strokeWidth={stroke ? 0.8 : 0}
      />
    </g>
  );
}

function RFWLetterR({ fill, stroke }: { fill: string; stroke?: string }) {
  return (
    <path
      d="M2 0 h34 c5 0 8 2 10 4 c2 2 3 5 3 10 c0 5 -1 8 -3 10 c-2 2 -5 4 -10 4 h-14 v28 h-20 v-56 z M22 18 h14 c3 0 4 -1 5 -2 c1 -1 1 -2 1 -3 c0 -1 0 -2 -1 -3 c-1 -1 -2 -2 -5 -2 h-14 v10 z"
      fill={fill}
      stroke={stroke}
      strokeWidth={stroke ? 0.5 : 0}
    />
  );
}

function RFWLetterF({ fill, stroke }: { fill: string; stroke?: string }) {
  return (
    <path
      d="M2 0 h52 v10 h-32 v12 h26 v10 h-26 v24 h-20 v-56 z"
      fill={fill}
      stroke={stroke}
      strokeWidth={stroke ? 0.5 : 0}
    />
  );
}

function RFWLetterW({ fill, stroke }: { fill: string; stroke?: string }) {
  return (
    <path
      d="M0 0 h18 l10 28 l10 -28 h18 l-20 56 h-16 l-20 -56 z"
      fill={fill}
      stroke={stroke}
      strokeWidth={stroke ? 0.5 : 0}
    />
  );
}

function WaveAccent({ fill }: { fill: string }) {
  return (
    <path
      d="M34 30 Q38 22 42 30 Q46 38 50 30 Q54 22 58 30"
      fill="none"
      stroke={fill}
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
}

export default function GlobalLogo({
  size = 64,
  variant = "fullColor",
  showText = false,
  className = "",
}: GlobalLogoProps) {
  const [prefersDark, setPrefersDark] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    setPrefersDark(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersDark(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const effectiveVariant = variant === "fullColor" && prefersDark ? "white" : variant;

  const isAnimated = effectiveVariant === "animated";
  const isFavicon = effectiveVariant === "favicon";
  const isSmall = size <= 32;

  const gradientStops = (
    <linearGradient id={GRADIENT_ID} x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stopColor="#2563EB" />
      <stop offset="50%" stopColor="#1D8CF8" />
      <stop offset="100%" stopColor="#06B6D4" />
    </linearGradient>
  );

  const getFill = () => {
    switch (effectiveVariant) {
      case "fullColor":
      case "animated":
        return `url(#${GRADIENT_ID})`;
      case "white":
        return "#FFFFFF";
      case "black":
        return "#020617";
      case "monoBlue":
        return "#2563EB";
      case "favicon":
        return "#2563EB";
      default:
        return `url(#${GRADIENT_ID})`;
    }
  };

  const getAccentFill = () => {
    switch (effectiveVariant) {
      case "fullColor":
      case "animated":
        return "#06B6D4";
      case "white":
        return "#06B6D4";
      case "black":
        return "#2563EB";
      case "monoBlue":
        return "#1D8CF8";
      case "favicon":
        return "#06B6D4";
      default:
        return "#06B6D4";
    }
  };

  const mainFill = getFill();
  const accentFill = getAccentFill();
  const baseSize = isFavicon ? 32 : isSmall ? 32 : size;
  const viewBox = isFavicon ? "0 0 32 32" : "0 0 120 56";

  if (isFavicon) {
    return (
      <svg
        width={baseSize}
        height={baseSize}
        viewBox={viewBox}
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient id={`${GRADIENT_ID}-fav`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#2563EB" />
            <stop offset="100%" stopColor="#06B6D4" />
          </linearGradient>
        </defs>
        <rect width="32" height="32" rx="6" fill="#020617" />
        <text
          x="16"
          y="22"
          textAnchor="middle"
          fontSize="18"
          fontWeight="800"
          fontFamily="Inter, system-ui, sans-serif"
          fill={`url(#${GRADIENT_ID}-fav)`}
        >
          R
        </text>
        <text
          x="16"
          y="22"
          textAnchor="middle"
          fontSize="18"
          fontWeight="800"
          fontFamily="Inter, system-ui, sans-serif"
          fill="none"
          stroke="url(#gradient-fav)"
          strokeWidth={0.5}
        >
          R
        </text>
      </svg>
    );
  }

  const textColor = effectiveVariant === "white" ? "#FFFFFF" : effectiveVariant === "black" ? "#020617" : "#64748B";

  return (
    <div className={`flex flex-col items-center ${className}`} style={{ width: size, height: showText ? size + 24 : size }}>
      <svg
        width={size}
        height={size * 0.47}
        viewBox={viewBox}
        xmlns="http://www.w3.org/2000/svg"
        className={isAnimated ? "animate-rfw-logo" : ""}
        style={{ display: "block" }}
      >
        <defs>
          {gradientStops}
          <filter id="rfwGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g transform={isAnimated ? "translate(0, 0)" : undefined}>
          <g transform="translate(0, 0)">
            <RFWLetterR fill={mainFill} stroke={effectiveVariant === "white" ? "#FFFFFF40" : undefined} />
          </g>
          <g transform="translate(40, 0)">
            <RFWLetterF fill={mainFill} stroke={effectiveVariant === "white" ? "#FFFFFF40" : undefined} />
          </g>
          <g transform="translate(82, 0)">
            <RFWLetterW fill={mainFill} stroke={effectiveVariant === "white" ? "#FFFFFF40" : undefined} />
          </g>
        </g>

        {isAnimated && (
          <g className="animate-rfw-wave">
            <WaveAccent fill={accentFill} />
          </g>
        )}
      </svg>

      {showText && (
        <span
          className="mt-2 text-center tracking-widest uppercase"
          style={{
            fontSize: Math.max(10, size * 0.14),
            fontWeight: 500,
            color: textColor,
            letterSpacing: "0.15em",
            fontFamily: "Inter, system-ui, sans-serif",
            opacity: 0.7,
          }}
        >
          RadioFrecuenciaWeb
        </span>
      )}
    </div>
  );
}

export function GlobalLogoMark({ size = 24, className = "" }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size * 0.47}
      viewBox="0 0 120 56"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ display: "block" }}
    >
      <defs>
        <linearGradient id="markGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#2563EB" />
          <stop offset="50%" stopColor="#1D8CF8" />
          <stop offset="100%" stopColor="#06B6D4" />
        </linearGradient>
      </defs>
      <g transform="translate(0, 0)">
        <RFWLetterR fill="url(#markGradient)" />
      </g>
      <g transform="translate(40, 0)">
        <RFWLetterF fill="url(#markGradient)" />
      </g>
      <g transform="translate(82, 0)">
        <RFWLetterW fill="url(#markGradient)" />
      </g>
    </svg>
  );
}

export function GlobalLogoAnimated({ size = 64, showText = false, className = "" }: { size?: number; showText?: boolean; className?: string }) {
  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div className="relative">
        <svg
          width={size}
          height={size * 0.47}
          viewBox="0 0 120 56"
          xmlns="http://www.w3.org/2000/svg"
          className="animate-rfw-logo"
          style={{ display: "block" }}
        >
          <defs>
            <linearGradient id="animGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#2563EB" />
              <stop offset="50%" stopColor="#1D8CF8" />
              <stop offset="100%" stopColor="#06B6D4" />
            </linearGradient>
            <filter id="animGlow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="1.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <g className="animate-rfw-letters">
            <g transform="translate(0, 0)">
              <RFWLetterR fill="url(#animGradient)" />
            </g>
            <g transform="translate(40, 0)">
              <RFWLetterF fill="url(#animGradient)" />
            </g>
            <g transform="translate(82, 0)">
              <RFWLetterW fill="url(#animGradient)" />
            </g>
          </g>
          <g className="animate-rfw-wave-accent">
            <WaveAccent fill="#06B6D4" />
          </g>
        </svg>
      </div>
      {showText && (
        <span className="mt-3 text-xs tracking-widest uppercase text-foreground-700/60 font-medium">
          RadioFrecuenciaWeb
        </span>
      )}
    </div>
  );
}